import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  CURRENT_ISSUER_DIVIDEND_EVENT_LIST_QUERY_KEY,
  DIVIDEND_BY_ID_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/dividends.tsx';
import {
  BulkGenerateReportRequest,
  GenerateBulkReportsResponse,
  GenerateReportRequest,
  GenerateReportResponse,
  GetBulkReportChildrenResponse,
  GetReportListResponse,
  GetReportsListV1SrReportsGetParams,
} from 'api';

export const REPORT_LIST_QUERY_KEY = 'vestra/report-list';
export const BULK_REPORT_CHILDREN_QUERY_KEY = 'vestra/bulk-report-children';
const REPORT_LIST_REFETCH_INTERVAL_MS = 1000 * 60 * 5;

interface DeleteReportMutationPayload {
  reportId: number;
  parentBulkReportId?: number;
  parentBulkReportRegisterId?: number;
  skipBulkChildrenInvalidation?: boolean;
}

export const useGetReportInfiniteList = ({
  payload,
  disabled = false,
}: {
  payload: GetReportsListV1SrReportsGetParams;
  disabled?: boolean;
}) => {
  const { page, ...data } = payload;

  return useInfiniteQuery<GetReportListResponse>({
    queryKey: [REPORT_LIST_QUERY_KEY, JSON.stringify(data)],
    staleTime: 999 * 60 * 60, // 1 hours
    queryFn: async ({ pageParam = 1 }) => {
      const response = await api.v1.getReportsListV1SrReportsGet({
        ...payload,
        page: pageParam || page,
      });
      return response.data;
    },
    enabled: !disabled,
    refetchInterval: REPORT_LIST_REFETCH_INTERVAL_MS,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
  });
};

export const useGetReportList = ({
  payload,
  disabled = false,
  onCompleted,
}: {
  payload: GetReportsListV1SrReportsGetParams;
  disabled?: boolean;
  onCompleted?: (data?: GetReportListResponse) => number | false;
}) => {
  return useQuery<GetReportListResponse>({
    queryKey: [REPORT_LIST_QUERY_KEY, JSON.stringify(payload)],
    staleTime: 999 * 60 * 60, // 1 hours
    queryFn: async () => {
      const response = await api.v1.getReportsListV1SrReportsGet(payload);
      return response.data;
    },
    enabled: !disabled,
    refetchInterval: onCompleted
      ? data => {
          return onCompleted(data);
        }
      : REPORT_LIST_REFETCH_INTERVAL_MS,
  });
};

export const useGetBulkReportChildren = (
  reportId?: number,
  registerId?: number,
  disabled = false,
) => {
  return useQuery<GetBulkReportChildrenResponse>({
    queryKey: [BULK_REPORT_CHILDREN_QUERY_KEY, reportId, registerId],
    queryFn: async () => {
      if (!reportId) throw new Error('Bulk report id is required');

      const response = await api.v1.getBulkReportChildrenV1SrReportsReportIdChildrenGet(
        { reportId },
        { headers: { 'xRegister-ID': registerId } },
      );
      return response.data;
    },
    enabled: !!reportId && !disabled,
  });
};

export const useCreateReportMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: GenerateReportResponse) => Promise<void> | void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: GenerateReportRequest) => {
      const response = await withErrorHandling(api.v1.createReportV1SrReportsPost(payload), {
        loading: 'Generating report...',
        success: 'Report generated successfully',
        setErrors,
      });
      return response.data;
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries([REPORT_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries({ queryKey: [DIVIDEND_BY_ID_QUERY_KEY] });
      await queryClient.invalidateQueries([CURRENT_ISSUER_DIVIDEND_EVENT_LIST_QUERY_KEY]);
      onSuccess && (await onSuccess(data));
    },
  });
};

export const useCreateBulkReportMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: GenerateBulkReportsResponse) => Promise<void> | void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: BulkGenerateReportRequest) => {
      const response = await withErrorHandling(
        api.v1.createBulkReportV1SrReportsBulkPost(payload, {
          headers: { 'xRegister-ID': payload.register_id },
        }),
        {
          loading: 'Generating reports...',
          success: 'Bulk report generation started. Download will start automatically when ready.',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries([REPORT_LIST_QUERY_KEY]);
      onSuccess && (await onSuccess(data));
    },
  });
};

export const useRegenerateBulkReportZipMutation = () => {
  return useMutation({
    mutationFn: async ({ reportId, registerId }: { reportId: number; registerId: number }) => {
      const response = await withErrorHandling(
        api.v1.regenerateBulkReportZipV1SrReportsReportIdRegenerateZipPost(
          { reportId },
          { headers: { 'xRegister-ID': registerId } },
        ),
        {
          loading: 'Regenerating ZIP...',
          success: 'ZIP regenerated successfully',
        },
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([REPORT_LIST_QUERY_KEY]);
    },
  });
};

export const useDeleteReportMutation = () => {
  return useMutation({
    mutationFn: async ({ reportId }: DeleteReportMutationPayload) => {
      const response = await withErrorHandling(
        api.v1.deleteReportV1SrReportsIdDelete({ id: reportId }),
        {
          loading: 'Deleting report...',
          success: 'Report deleted successfully',
        },
      );
      return response.data;
    },
    onSuccess: async (_, payload) => {
      await queryClient.invalidateQueries([REPORT_LIST_QUERY_KEY]);

      if (payload.skipBulkChildrenInvalidation) return;

      if (payload.parentBulkReportId) {
        await queryClient.invalidateQueries([
          BULK_REPORT_CHILDREN_QUERY_KEY,
          payload.parentBulkReportId,
          payload.parentBulkReportRegisterId,
        ]);
        return;
      }

      await queryClient.invalidateQueries([BULK_REPORT_CHILDREN_QUERY_KEY]);
    },
  });
};
